import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { hydrateCartUpsell, invalidateRecommendations } from '@theme/cart-recommendations';

function getSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);
}

function dispatchError(error, fallback) {
  document.dispatchEvent(
    new CartErrorEvent({
      error: error?.message || fallback,
      code: 'INVALID',
    })
  );
}

/**
 * Reports cart upsell activity to whatever analytics the store already loads.
 *
 * There is no historical figure for what the removed app earned, so the in-house
 * replacement has to be measurable from the day it ships - otherwise a later move in
 * cart conversion can be neither attributed nor defended.
 *
 * Every sink is wrapped individually. Analytics must never be able to break the cart:
 * a vendor script that is blocked, still loading, or throwing is not a reason for an
 * add-to-cart to fail.
 */
function trackUpsell(event, detail = {}) {
  const payload = { surface: 'cart-upsell', event, ...detail };

  try {
    window.Shopify?.analytics?.publish?.(`cart_upsell_${event}`, payload);
  } catch {
    /* vendor unavailable */
  }

  try {
    window.LO?.events?.track?.(`cart_upsell_${event}`, payload);
  } catch {
    /* vendor unavailable */
  }

  // Always emit a DOM event, so the data is observable even when no vendor is
  // present - this is what makes the contract and manual verification possible.
  document.dispatchEvent(new CustomEvent('cart-upsell:track', { detail: payload }));
}

/**
 * Shared add-to-cart bridge for merchant sections that do not use product-form.
 *
 * @param {object} options
 * @param {Array<{merchandiseId: string|number, quantity?: number}>} options.lines
 * @param {'product'|'cart'|'dialog'} [options.context]
 * @param {string} [options.source]
 * @param {string} [options.sourceId]
 * @returns {Promise<void>}
 */
export async function merchantAddToCart({ lines, context = 'product', source, sourceId }) {
  if (!lines?.length) {
    throw new Error('No cart lines provided');
  }

  const sections = getSectionIds();
  const deferredPromise = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context,
      lines: lines.map((line) => ({
        merchandiseId: String(line.merchandiseId),
        quantity: Number(line.quantity) || 1,
      })),
      promise: deferredPromise.promise,
      detail: {
        source,
        sourceId,
      },
    })
  );

  const payload = {
    items: lines.map((line) => ({
      id: Number(line.merchandiseId),
      quantity: Number(line.quantity) || 1,
    })),
    sections: sections.join(','),
    sections_url: window.location.pathname,
  };

  const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const addResult = await response.json();
  if (!response.ok || addResult.status) {
    const error = new Error(addResult.description || addResult.message || 'Add to cart failed');
    deferredPromise.reject(error);
    dispatchError(error, 'Add to cart failed');
    throw error;
  }

  const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
  const cart = await cartResponse.json();

  deferredPromise.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      sections: addResult.sections,
      items: cart.items,
      itemCount: cart.item_count,
      source,
      sourceId,
      didError: false,
    },
  });
}

if (typeof window !== 'undefined') {
  window.merchantAddToCart = merchantAddToCart;
}

async function addUpsell(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.disabled) return;

  button.disabled = true;
  button.textContent = 'Adding...';

  try {
    await merchantAddToCart({
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      context: 'cart',
      source: 'cart-upsell',
      sourceId: 'cart-upsell',
    });

    trackUpsell('add', { variantId, source: 'cart-upsell' });

    button.textContent = 'Added';
    setTimeout(() => {
      button.disabled = false;
      button.textContent = '+ Add';
    }, 1200);
  } catch {
    button.disabled = false;
    button.textContent = '+ Add';
  }
}

/**
 * Sends the shopper to the product page to pick a variant for a multi-variant upsell.
 *
 * This was meant to reuse the theme's quick-add modal (rendered globally from
 * layout/theme.liquid) instead of a full navigation. That approach was tried and
 * reverted: cloning the fetched product page's content into the modal via
 * `doc.querySelector('#quick-add-modal-content, .product-details, main')` does not
 * select the modal container as intended - a comma-separated selector list resolves
 * to the first match in DOCUMENT ORDER, not selector priority, and `<main>` precedes
 * `#quick-add-modal-content` on every product page. The result was the entire page's
 * `<main>` - script tags included - being dropped into the live modal. Verified live:
 * one of those re-executed scripts navigated the whole tab away from the storefront
 * to the production domain a moment after the modal opened. A plain navigation is
 * what the bundle branch of quick-add.liquid already does for a comparable case, and
 * is the safe choice here - see the commit message for the full account.
 */
async function openUpsellVariantPicker(button) {
  const productUrl = button.dataset.productUrl;
  if (!productUrl) return;
  window.location.href = productUrl;
}

async function updateSellingPlan(button, sellingPlanId) {
  if (button.classList.contains('is-loading')) return;

  const lineKey = button.dataset.lineKey;
  const quantity = Number(button.dataset.quantity);
  if (!lineKey || !quantity) return;

  const sections = getSectionIds();
  const deferredPromise = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: lineKey, quantity }],
      promise: deferredPromise.promise,
    })
  );

  button.classList.add('is-loading');
  button.disabled = true;

  try {
    const changeBody = {
      id: lineKey,
      quantity,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    };

    // Shopify's cart/change.js only detaches a line's selling plan when `selling_plan`
    // is explicitly sent as null - omitting the key entirely leaves the existing plan
    // attached, which is what made "Unsubscribe" (called with no sellingPlanId) a no-op.
    changeBody.selling_plan = sellingPlanId || null;

    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(changeBody),
    });
    const cart = await response.json();
    if (!response.ok || cart.errors) {
      throw new Error(cart.description || cart.message || cart.errors || 'Subscription update failed');
    }

    deferredPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'cart-subscription',
        didError: false,
      },
    });
  } catch (error) {
    deferredPromise.reject(error);
    dispatchError(error, 'Subscription update failed');
    button.classList.remove('is-loading');
    button.disabled = false;
  }
}

if (!window.__cartMerchantActionsBound) {
  window.__cartMerchantActionsBound = true;

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const bundleToggle = event.target.closest('[data-cart-bundle-toggle]');
    if (bundleToggle) {
      const accordion = document.getElementById(bundleToggle.getAttribute('aria-controls'));
      if (accordion instanceof HTMLDetailsElement) {
        accordion.open = !accordion.open;
        bundleToggle.setAttribute('aria-expanded', String(accordion.open));
      }
      return;
    }

    const upsellButton = event.target.closest('.cart-upsell-card__add');
    if (upsellButton instanceof HTMLButtonElement) {
      void addUpsell(upsellButton);
      return;
    }

    const chooseButton = event.target.closest('.cart-upsell-card__choose');
    if (chooseButton instanceof HTMLButtonElement) {
      void openUpsellVariantPicker(chooseButton);
      return;
    }

    const planOption = event.target.closest('.cart-selling-plan-option');
    if (planOption instanceof HTMLButtonElement) {
      const frequencies = planOption.closest('.cart-subscription-card__frequencies');
      frequencies?.querySelectorAll('.cart-selling-plan-option').forEach((option) => {
        const selected = option === planOption;
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-checked', String(selected));
      });
      return;
    }

    const subscriptionConfirm = event.target.closest('.cart-subscription-confirm');
    if (subscriptionConfirm instanceof HTMLButtonElement) {
      const selectedOption = subscriptionConfirm
        .closest('.cart-subscription-card')
        ?.querySelector('.cart-selling-plan-option.is-selected');
      if (selectedOption instanceof HTMLElement) {
        void updateSellingPlan(subscriptionConfirm, Number(selectedOption.dataset.sellingPlan));
      }
      return;
    }

    const subscriptionToggle = event.target.closest('.cart-subscribe-save');
    if (!(subscriptionToggle instanceof HTMLButtonElement)) return;

    if (subscriptionToggle.dataset.isSubscription === 'true') {
      void updateSellingPlan(subscriptionToggle);
      return;
    }

    const options = subscriptionToggle
      .closest('.cart-subscription-box')
      ?.querySelector('.cart-selling-plan-options');
    if (options instanceof HTMLElement) {
      options.hidden = !options.hidden;
    }
  });

  /**
   * Hydrates the cart upsell carousel with per-cart recommendations once the
   * cart drawer opens. `theme-drawer:open` (DrawerOpenEvent) is the theme's
   * own signal for this — assets/cart-drawer.js and assets/header-actions.js
   * already listen for it the same way, scoped to a specific drawer by
   * checking `event.target.id` since the event bubbles from whichever
   * <theme-drawer> opened. No MutationObserver is needed: the event fires on
   * every real open. It does NOT fire on the sessionStorage restore path
   * (snippets/theme-drawer.liquid dispatches a separate `theme-drawer:restoring`
   * for that case) — that path is picked up by the [open] check at the end of
   * this block instead.
   */
  document.addEventListener(DrawerOpenEvent.eventName, (event) => {
    if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
    if (event.target.getAttribute('data-dynamic-upsell') === 'false') return;
    void hydrateCartUpsell(event.target);
  });

  // Any cart line change can change which recommendations apply — invalidate
  // the cache so the next drawer-open re-fetches instead of serving stale
  // HTML. This is deliberately unconditional (not gated on action/success):
  // the cache key in cart-recommendations.js already dedupes by cart token +
  // item count, so an extra invalidation here is cheap, and it also covers
  // cases that key wouldn't catch on its own (e.g. removing one line and
  // adding a different one at the same total quantity).
  document.addEventListener(CartLinesUpdateEvent.eventName, invalidateRecommendations);

  // Impressions: count the cards actually on screen once the drawer has opened and
  // any recommendation swap has settled. Measured after hydration rather than before,
  // so the count reflects what the shopper really saw.
  document.addEventListener(DrawerOpenEvent.eventName, (event) => {
    if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
    requestAnimationFrame(() => {
      const cards = event.target.querySelectorAll('.cart-upsell-card');
      if (cards.length) trackUpsell('impression', { count: cards.length });
    });
  });

  // Checkout reached with an upsell on screen. Pairs with the add event to give a
  // crude funnel without needing any vendor-side configuration.
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('#checkout')) return;
    const shown = document.querySelectorAll('.cart-upsell-card').length;
    if (shown) trackUpsell('checkout', { shown });
  });

  // Drawer restored from sessionStorage rather than opened by the shopper.
  // snippets/theme-drawer.liquid sets [open] and dispatches its own
  // `theme-drawer:restoring` from an inline script during parsing — that event
  // neither bubbles nor has any listener yet, since this module is deferred. So
  // both DrawerOpenEvent listeners above miss it entirely and the drawer kept
  // showing the server-rendered list. Read the attribute instead, the same way
  // assets/cart-drawer.js handles this.
  //
  // Gated on [open], so an ordinary page load still fetches nothing —
  // cart-drawer-contract asserts that ("no recommendations fetch on page load").
  const restoredCartDrawer = document.querySelector('#cart-drawer[open]');
  if (restoredCartDrawer) {
    if (restoredCartDrawer.getAttribute('data-dynamic-upsell') !== 'false') {
      void hydrateCartUpsell(restoredCartDrawer);
    }
    requestAnimationFrame(() => {
      const cards = restoredCartDrawer.querySelectorAll('.cart-upsell-card');
      if (cards.length) trackUpsell('impression', { count: cards.length });
    });
  }
}
